<script lang="ts">
  import { reveal } from 'svelte-reveal';

  import { useWallet } from '@do/svelte-web3-manager';

  import { Accordion } from '@shared/ui/accordion';
  import { Checkbox } from '@shared/ui/checkbox';
  import { Icon } from '@shared/ui/icon';
  import { InsufficientFundsHint } from '@shared/ui/insufficient-funds-hint';
  import { Tooltip } from '@shared/ui/tooltip';
  import { getPrice } from '@shared/utils/blockchain';

  import { PurchaseState, purchaseModel } from '@entities/purchase';

  import type { SaleOrder } from '@features/purchase/purchase-artwork';
  import { PurchaseAgreement } from '@features/purchase/purchase-info';

  import { artworkPurchaseInfo, purchasePrice } from '../model';

  export let saleOrder: NonNullable<SaleOrder>;
  export let price = 0;
  export let checked: boolean;
  export let error = '';

  const wallet = useWallet();

  $: priceWithBuyerFee = (price * saleOrder?.buyer_fee_percent) / 100;
  $: totalPrice = price + priceWithBuyerFee + saleOrder?.storage_fee_usd;
  $: isCollapsibleBlockOpen =
    $artworkPurchaseInfo &&
    ($purchaseModel.state === PurchaseState.Default || $purchaseModel.state === PurchaseState.InsufficientFunds);
  $: isPurchaseInProgress =
    $purchaseModel.state === PurchaseState.InProgress ||
    $purchaseModel.state === PurchaseState.Pending ||
    $purchaseModel.state === PurchaseState.Success ||
    $purchaseModel.state === PurchaseState.Approval;

  const checkIfInsufficientFunds = (usdc: number, price: number) => {
    const isInsufficientFunds = usdc < price;

    purchaseModel.update(({ state }) => {
      const expectedState = isInsufficientFunds ? PurchaseState.InsufficientFunds : state;

      return { state: expectedState };
    });
  };

  $: checkIfInsufficientFunds(Number($wallet?.balance.usdc), totalPrice);
</script>

<article>
  <p class="text-default-500 dark:text-dark-700 mb-2 text-base">Purchase price:</p>

  <p class="inline-flex items-end gap-1.5">
    <span class="flex items-center gap-0.5 text-center text-3xl font-medium leading-[24px]">
      <Icon name="usdc" width="{28}" height="{28}" classes="pt-[1.5px]" />

      {#if isPurchaseInProgress}
        <div use:reveal="{{ transition: 'fly', y: 0, duration: 300, opacity: 0 }}">
          {getPrice($purchasePrice || totalPrice)}
        </div>
      {:else}
        <div use:reveal="{{ transition: 'fly', y: 0, duration: 300, opacity: 0 }}">
          {getPrice(price)}
        </div>
      {/if}
    </span>

    <span class="text-base leading-[20px]">USDC</span>
  </p>

  <Accordion isOpen="{isCollapsibleBlockOpen}">
    <div class="dark:text-dark-700 mb-3 mt-5 flex items-center justify-between text-sm">
      <div class="flex items-center gap-1">
        <p>Buyer premium ({saleOrder.buyer_fee_percent}%)</p>

        <Tooltip>
          <p class="w-[200px]">
            This is a commission paid by the buyer to the platform, which is added to the final purchase price of the
            auctioned item.
          </p>
        </Tooltip>
      </div>

      <p>{getPrice(priceWithBuyerFee)} USDC</p>
    </div>

    <div class="dark:text-dark-700 mb-4 flex items-center justify-between text-sm">
      <p class="text-balance">
        Blockchain storage exp. for 100 years
        <Tooltip contentWrapperClasses="align-middle">
          <p class="w-[200px]">
            All artwork assets will be stored as files in Blockchain Storage, guaranteeing data durability and security
            for a century.
          </p>
        </Tooltip>
      </p>

      <p class="shrink-0">{saleOrder.storage_fee_usd} USDC</p>
    </div>

    <article class="dark:border-t-dark-500 border-t-default-320 flex items-center justify-between border-t py-4">
      <p class="text-base font-bold">Total:</p>

      <div class="flex items-center gap-1 text-[24px] leading-none">
        <Icon name="usdc" width="{18}" height="{18}" classes="pt-[0.5px] pb-[0.5px]" />

        <p class="flex gap-1">
          <span class="text-[18px] font-semibold leading-[24px]">
            {getPrice(totalPrice)}
          </span>

          <span class="self-end text-sm font-semibold leading-[21px]">USDC</span>
        </p>
      </div>
    </article>

    <Checkbox name="confirm" bind:checked {error}>
      <PurchaseAgreement />
    </Checkbox>

    {#if $purchaseModel.state === PurchaseState.InsufficientFunds}
      <InsufficientFundsHint>You will need both USDC and ETH to make the purchase.</InsufficientFundsHint>
    {/if}
  </Accordion>
</article>
